import { useState, useEffect } from 'react';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';
import { Button } from '@/components/base/buttons/button';
import { Tabs } from '@/components/application/tabs/tabs';
import { X, Plus } from '@untitledui/icons';
import type { EmailTemplate, CreateEmailTemplateData, UpdateEmailTemplateData } from '@/types/quote';
import { useTranslation } from 'react-i18next';
// Note: render from @react-email/render is server-side only
// For client-side preview, we'll show formatted text instead

interface EmailTemplateEditorProps {
    template?: EmailTemplate;
    onSubmit: (data: CreateEmailTemplateData | UpdateEmailTemplateData) => void;
    onCancel: () => void;
}

export const EmailTemplateEditor = ({ template, onSubmit, onCancel }: EmailTemplateEditorProps) => {
    const { t } = useTranslation('pages:quotes');
    const [activeTab, setActiveTab] = useState<'edit' | 'preview'>('edit');
    const [formData, setFormData] = useState({
        name: template?.name || '',
        subject: template?.subject || '',
        body: template?.body || '',
        draftingInstructions: template?.draftingInstructions || [],
    });
    const [previewHtml, setPreviewHtml] = useState('');

    const handleAddInstruction = () => {
        setFormData({
            ...formData,
            draftingInstructions: [
                ...formData.draftingInstructions,
                {
                    id: `temp-${Date.now()}`,
                    instruction: '',
                    order: formData.draftingInstructions.length + 1,
                },
            ],
        });
    };

    const handleRemoveInstruction = (id: string) => {
        setFormData({
            ...formData,
            draftingInstructions: formData.draftingInstructions.filter(inst => inst.id !== id),
        });
    };

    const handleInstructionChange = (id: string, value: string) => {
        setFormData({
            ...formData,
            draftingInstructions: formData.draftingInstructions.map(inst =>
                inst.id === id ? { ...inst, instruction: value } : inst
            ),
        });
    };

    const generatePreview = () => {
        try {
            // Simple preview - replace variables with sample data
            const sampleData = {
                recipientName: 'Mario Rossi',
                product: 'avocado',
                origin: 'Lima, Perù',
                destination: 'Milano, Italia',
                quantity: '500',
                unit: 'kg',
                pickupDate: '15 marzo 2024',
                deliveryDate: '25 marzo 2024',
                basePrice: '2.500',
                currency: 'EUR',
                additionalComponents: '500 EUR',
                totalPrice: '3.000',
                quoteId: 'QUOTE-001',
                details: 'Dettagli del trasporto...',
            };

            let previewSubject = formData.subject;
            let previewBody = formData.body;

            Object.entries(sampleData).forEach(([key, value]) => {
                const regex = new RegExp(`{{${key}}}`, 'g');
                previewSubject = previewSubject.replace(regex, value);
                previewBody = previewBody.replace(regex, value);
            });

            // Create a simple HTML preview (render is server-side only)
            const emailHtml = `
                <!DOCTYPE html>
                <html>
                <head>
                    <meta charset="utf-8">
                    <style>
                        body { font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; line-height: 1.6; background-color: #ffffff; color: #000000; }
                        h1 { font-size: 24px; margin-bottom: 20px; color: #000000; }
                        p { font-size: 14px; white-space: pre-wrap; color: #000000; }
                    </style>
                </head>
                <body style="background-color: #ffffff; color: #000000;">
                    <h1>${previewSubject}</h1>
                    <p>${previewBody.replace(/\n/g, '<br>')}</p>
                </body>
                </html>
            `;

            setPreviewHtml(emailHtml);
        } catch (error) {
            console.error('Error generating preview:', error);
            setPreviewHtml('<p>Errore nella generazione del preview</p>');
        }
    };

    useEffect(() => {
        if (activeTab === 'preview' && formData.body) {
            generatePreview();
        }
    }, [activeTab, formData.body, formData.subject]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const instructions = formData.draftingInstructions
            .filter(inst => inst.instruction.trim() !== '')
            .map((inst, idx) => ({ ...inst, order: idx + 1 }));

        // Extract variables from body
        const variableMatches = formData.body.match(/{{(\w+)}}/g) || [];
        const variables = variableMatches.map(match => match.replace(/[{}]/g, ''));

        onSubmit({
            ...formData,
            draftingInstructions: instructions.map(({ id, ...rest }) => rest),
            variables: [...new Set(variables)],
        });
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <Input
                label={t('settings.templates.form.name')}
                placeholder={t('settings.templates.form.namePlaceholder')}
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                isRequired
                size="md"
            />

            <Input
                label={t('settings.templates.form.subject')}
                placeholder={t('settings.templates.form.subjectPlaceholder')}
                value={formData.subject}
                onChange={(value) => setFormData({ ...formData, subject: value })}
                isRequired
                size="md"
                hint={t('settings.templates.form.subjectHint')}
            />

            <Tabs selectedKey={activeTab} onSelectionChange={(key) => setActiveTab(key as 'edit' | 'preview')}>
                <Tabs.List items={[
                    { id: 'edit', label: t('settings.templates.form.editTab') },
                    { id: 'preview', label: t('settings.templates.form.previewTab') },
                ]}>
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                <Tabs.Panel id="edit">
                    <TextArea
                        label={t('settings.templates.form.body')}
                        placeholder={t('settings.templates.form.bodyPlaceholder')}
                        value={formData.body}
                        onChange={(value) => setFormData({ ...formData, body: value })}
                        isRequired
                        rows={12}
                        hint={t('settings.templates.form.bodyHint')}
                    />
                </Tabs.Panel>

                <Tabs.Panel id="preview">
                    <div className="rounded-lg bg-white p-4 border border-secondary">
                        {previewHtml ? (
                            <iframe
                                srcDoc={previewHtml}
                                className="w-full h-96 border-0 rounded bg-white"
                                title="Email Preview"
                            />
                        ) : (
                            <p className="text-sm text-tertiary text-center py-8">
                                {t('settings.templates.form.noPreview')}
                            </p>
                        )}
                    </div>
                </Tabs.Panel>
            </Tabs>

            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-primary">
                        {t('settings.templates.form.draftingInstructions')}
                    </label>
                    <Button
                        type="button"
                        onClick={handleAddInstruction}
                        color="secondary"
                        size="sm"
                        iconLeading={Plus}
                    >
                        {t('settings.templates.form.addInstruction')}
                    </Button>
                </div>
                <div className="space-y-2">
                    {formData.draftingInstructions.map((instruction) => (
                        <div key={instruction.id} className="flex items-center gap-2">
                            <TextArea
                                placeholder={t('settings.templates.form.instructionPlaceholder')}
                                value={instruction.instruction}
                                onChange={(value) => handleInstructionChange(instruction.id, value)}
                                rows={2}
                                className="flex-1"
                            />
                            <Button
                                type="button"
                                onClick={() => handleRemoveInstruction(instruction.id)}
                                color="primary-destructive"
                                size="sm"
                                iconLeading={X}
                            />
                        </div>
                    ))}
                    {formData.draftingInstructions.length === 0 && (
                        <p className="text-sm text-tertiary text-center py-4">
                            {t('settings.templates.form.noInstructions')}
                        </p>
                    )}
                </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary">
                <Button
                    type="button"
                    onClick={onCancel}
                    color="secondary"
                    size="md"
                >
                    {t('buttons.cancel', { ns: 'common' })}
                </Button>
                <Button
                    type="submit"
                    color="primary"
                    size="md"
                >
                    {template ? t('buttons.update', { ns: 'common' }) : t('buttons.create', { ns: 'common' })}
                </Button>
            </div>
        </form>
    );
};

